/**
 * API helpers for CV upload and confirmation.
 */

import { getCSRFToken } from "./advisor_api"

const REQUEST_TIMEOUT_MS = 60000

/**
 * Upload a CV file for extraction.
 * @param {string} url - The upload endpoint
 * @param {File} file - The PDF file to upload
 * @returns {Promise<Object>} Extracted data response
 */
export async function uploadCv(url, file) {
  const formData = new FormData()
  formData.append("cv_file", file)

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": getCSRFToken(),
        Accept: "application/json"
      },
      body: formData,
      signal: controller.signal
    })

    const body = await response.json()
    if (!response.ok) {
      return { error: body.error || `Server error (${response.status})`, ...body }
    }
    return body
  } finally {
    clearTimeout(timeout)
  }
}

/**
 * Confirm and save extracted CV data to memories.
 * @param {string} url - The confirm endpoint
 * @param {Object} extractedData - The reviewed/edited extracted data
 * @param {boolean} consent - Whether user consented to save
 * @returns {Promise<Object>} Confirmation response
 */
export async function confirmCv(url, extractedData, consent) {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCSRFToken(),
        Accept: "application/json"
      },
      body: JSON.stringify({
        extracted_data: extractedData,
        consent: consent ? "true" : "false"
      }),
      signal: controller.signal
    })

    const body = await response.json()
    if (!response.ok) {
      return { error: body.error || `Server error (${response.status})`, ...body }
    }
    return body
  } finally {
    clearTimeout(timeout)
  }
}
